/**
 * @fileoverview Recherche par plaque et sélection des contraventions.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  ContraventionCard,
  FormField,
  Input,
  PaymentSummary,
  PortalState,
  cn,
  formatPlate,
} from '@gnpc/ui';
import { LayoutGrid, List, Search } from 'lucide-react';
import { MOCK_CONTRAVENTIONS, MOCK_PLATE } from '../data/mock.ts';

type SearchUiState = 'results' | 'empty' | 'pending' | 'error';

/**
 * Disposition de la liste des résultats. Les deux variantes cohabitent :
 * `liste` (une colonne, lecture ligne à ligne) et `grille` (deux colonnes
 * dès `sm`, balayage plus dense quand la plaque porte beaucoup d'amendes).
 */
type SearchResultsView = 'liste' | 'grille';

const SEARCH_LOADING_MS = 7200;
const SEARCH_AWARENESS_INTERVAL_MS = 2200;

/**
 * Bornes FR-03 d'une transaction : au plus 10 liquidations et
 * 2 000 000 XOF cumulés (limite « Operation Limits » Paymetrust — la plus
 * restrictive s'applique). Appliquées ICI, pas seulement affichées : le
 * backend revalide, mais l'UI ne doit pas laisser construire un panier
 * impossible.
 */
const MAX_SELECTED_LIQUIDATIONS = 10;
const MAX_TOTAL_AMOUNT_XOF = 2_000_000;

const SEARCH_AWARENESS_MESSAGES = [
  {
    title: 'Respectez les limitations',
    description:
      "Adapter sa vitesse réduit les distances de freinage et protège les piétons comme les passagers.",
  },
  {
    title: 'Le feu rouge protège tout le monde',
    description:
      "Marquer l'arrêt au feu et au stop évite les collisions aux intersections les plus fréquentées.",
  },
  {
    title: 'Gardez les yeux sur la route',
    description:
      'Un appel ou un message peut attendre. Au volant, chaque seconde d’attention compte.',
  },
] as const;

/**
 * Formats acceptés dans le champ de recherche (tolérants au séparateur —
 * espace, tiret ou rien) : plaque « AB-572-GG » (2 lettres · 3 chiffres ·
 * 2 lettres), plaque « 1234-IT-12 » (4 chiffres · 2 lettres · 2 chiffres),
 * ou n° de liquidation « LIQ-921555 ». Le backend revalidera au branchement.
 */
const SEARCH_PATTERN =
  /^(?:[A-Z]{2}[-\s]?\d{3}[-\s]?[A-Z]{2}|\d{4}[-\s]?[A-Z]{2}[-\s]?\d{2}|LIQ[-\s]?\d{4,})$/;

function isValidSearch(value: string): boolean {
  return SEARCH_PATTERN.test(value.trim().toUpperCase());
}

function getSearchUiState(value: string | null): SearchUiState {
  if (value === 'empty' || value === 'pending' || value === 'error') return value;
  return 'results';
}

export function SearchPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const uiState = getSearchUiState(params.get('state'));
  /**
   * La disposition SURVIT PAR L'URL (`?vue=grille`), comme les autres états
   * de la page (règle du guide : pas de storage navigateur). Défaut : liste.
   */
  const resultsView: SearchResultsView = params.get('vue') === 'grille' ? 'grille' : 'liste';

  const setResultsView = (view: SearchResultsView) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (view === 'grille') next.set('vue', 'grille');
        else next.delete('vue');
        return next;
      },
      { replace: true },
    );
  };
  // Champ de recherche unique : le citoyen y saisit soit une plaque, soit un
  // numéro de liquidation (LIQ-…). Pas de masque strict — les deux formats
  // cohabitent dans le même champ, on prend ce que l'usager a sous la main.
  const [query, setQuery] = useState(formatPlate(MOCK_PLATE));
  const [searched, setSearched] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [awarenessIndex, setAwarenessIndex] = useState(0);
  // Présélection limitée aux 3 premières lignes : le mock en compte 12,
  // et tout présélectionner violerait d'emblée le plafond FR-03 (max 10).
  //
  // Affordance de démo/captures (même esprit que `?state=`) : `?demo=plafond`
  // présélectionne les 10 amendes les plus chères pour illustrer le
  // dépassement du plafond de 2 000 000 F CFA sans manipulation manuelle.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => {
    if (params.get('demo') === 'plafond') {
      return new Set(
        [...MOCK_CONTRAVENTIONS]
          .sort((a, b) => b.amount - a.amount)
          .slice(0, MAX_SELECTED_LIQUIDATIONS)
          .map((item) => item.liquidationNumber),
      );
    }
    return new Set(MOCK_CONTRAVENTIONS.slice(0, 3).map((item) => item.liquidationNumber));
  });
  const contraventions = useMemo(
    () => (uiState === 'results' ? MOCK_CONTRAVENTIONS : []),
    [uiState],
  );

  const selectedItems = useMemo(
    () => contraventions.filter((item) => selected.has(item.liquidationNumber)),
    [contraventions, selected],
  );
  const totalAmount = selectedItems.reduce((sum, item) => sum + item.amount, 0);
  const exceedsAmountCap = totalAmount > MAX_TOTAL_AMOUNT_XOF;
  const canContinue = selectedItems.length > 0 && !exceedsAmountCap;
  /**
   * La sélection SURVIT PAR L'URL (règle du guide : pas de storage
   * navigateur) — la page paiement relit `?liq=` pour reconstruire
   * exactement le panier choisi ici.
   */
  const paymentHref = `/paiement?liq=${selectedItems
    .map((item) => encodeURIComponent(item.liquidationNumber))
    .join(',')}`;
  const showPendingState = searched && (isSearching || uiState === 'pending');
  const showPaymentSummary = searched && !showPendingState && contraventions.length > 0;
  const activeAwarenessMessage =
    SEARCH_AWARENESS_MESSAGES[awarenessIndex] ?? SEARCH_AWARENESS_MESSAGES[0]!;

  useEffect(() => {
    if (!isSearching) return undefined;

    const startedAt = Date.now();
    const progressInterval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const progress = 18 + Math.round((elapsed / SEARCH_LOADING_MS) * 74);
      setSearchProgress(Math.min(progress, 92));
    }, 180);
    const awarenessInterval = window.setInterval(() => {
      setAwarenessIndex((current) => (current + 1) % SEARCH_AWARENESS_MESSAGES.length);
    }, SEARCH_AWARENESS_INTERVAL_MS);
    const completionTimeout = window.setTimeout(() => {
      setSearchProgress(100);
      setIsSearching(false);
      setSearched(true);
    }, SEARCH_LOADING_MS);

    return () => {
      window.clearInterval(progressInterval);
      window.clearInterval(awarenessInterval);
      window.clearTimeout(completionTimeout);
    };
  }, [isSearching]);

  const toggle = (liq: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(liq)) {
        next.delete(liq);
        return next;
      }
      // FR-03 : maximum 10 liquidations par transaction — l'ajout au-delà
      // est bloqué (la désélection reste toujours possible).
      if (next.size >= MAX_SELECTED_LIQUIDATIONS) return current;
      next.add(liq);
      return next;
    });
  };

  const startSearch = () => {
    setSearched(true);
    setSearchProgress(18);
    setAwarenessIndex(0);
    setIsSearching(true);
  };

  // Validation de format (plaque ou n° de liquidation) : la recherche ne se
  // lance que sur une saisie reconnue ; sinon on signale l'erreur de format.
  const trimmedQuery = query.trim();
  const showFormatError = trimmedQuery.length > 0 && !isValidSearch(trimmedQuery);
  const canSearch = isValidSearch(trimmedQuery);
  const searchButton = (
    <Button
      type="button"
      size="lg"
      className="w-full sm:w-auto"
      disabled={!canSearch || isSearching}
      onClick={startSearch}
    >
      <Search />
      {isSearching ? 'Recherche...' : 'Rechercher'}
    </Button>
  );

  return (
    <div className="bg-slate-50">
      <section className="portal-animated-hero bg-quipux-navy-950">
        <div className="relative z-10 mx-auto max-w-5xl px-4 pb-28 pt-12 md:pb-32 md:pt-16">
          <header className="max-w-2xl space-y-2">
            <p className="text-quipux-orange-400 text-sm font-medium">Amendes et services de transport</p>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Trouver vos amendes et formalités
            </h1>
            <p className="text-sm leading-6 text-white/70">
              Recherchez par plaque d'immatriculation ou par numéro de liquidation pour consulter et
              régler vos amendes et vos formalités de transport.
            </p>
          </header>
        </div>
      </section>

      <div
        className={
          showPaymentSummary
            ? 'relative z-10 mx-auto -mt-20 grid max-w-5xl gap-6 px-4 pb-44 lg:grid-cols-[minmax(0,1fr)_360px] lg:pb-10'
            : 'relative z-10 mx-auto -mt-20 grid max-w-3xl gap-6 px-4 pb-10'
        }
      >
        <div className="space-y-5">
          <section className="border-border bg-card rounded-lg border p-5">
            <FormField
              label="Numéro de plaque ou de liquidation"
              required
              tooltip="Formats acceptés : plaque « AB-572-GG » ou « 1234-IT-12 », ou numéro de liquidation « LIQ-921555 »."
              hint="Le numéro de liquidation figure sur votre avis de contravention."
              error={
                showFormatError
                  ? 'Format non reconnu. Exemples : AB-572-GG, 1234-IT-12 ou LIQ-921555.'
                  : undefined
              }
              action={searchButton}
            >
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value.toUpperCase())}
                placeholder="AB-572-GG · 1234-IT-12 · LIQ-921555"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                aria-label="Numéro de plaque ou de liquidation"
                className="h-12 font-mono text-lg font-semibold uppercase tracking-normal"
              />
            </FormField>
          </section>

          {showPendingState && (
            <PortalState
              status="pending"
              title="Recherche des contraventions"
              progressValue={isSearching ? searchProgress : 42}
            >
              <div className="space-y-3">
                <p>Nous vérifions votre recherche « {query} » avant d'afficher les résultats.</p>
                <div className="border-quipux-blue-200 bg-white rounded-md border p-3">
                  <p className="text-quipux-navy-950 text-sm font-semibold">
                    {activeAwarenessMessage.title}
                  </p>
                  <p className="mt-1 text-sm leading-6">{activeAwarenessMessage.description}</p>
                </div>
              </div>
            </PortalState>
          )}

          {searched && !showPendingState && uiState === 'error' && (
            <PortalState
              status="error"
              title="Service Quipux momentanément indisponible"
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsSearching(false);
                    setSearched(false);
                  }}
                >
                  Modifier la recherche
                </Button>
              }
              secondaryAction={
                <Button type="button" onClick={startSearch}>
                  Réessayer
                </Button>
              }
            >
              Le portail ne peut pas consulter les contraventions pour le moment. Aucun paiement n'a
              été lancé.
            </PortalState>
          )}

          {searched && !showPendingState && uiState === 'results' && contraventions.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-quipux-navy-950 text-lg font-semibold">
                    {contraventions.length} amendes trouvées
                  </h2>
                  <p className="text-muted-foreground text-sm">Recherche : {query}</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Commutateur liste / grille 2 colonnes. Masqué sous `sm` :
                    * en mobile la grille retombe de toute façon sur une
                    * colonne, le contrôle n'y aurait aucun effet. */}
                  <div
                    role="group"
                    aria-label="Disposition des résultats"
                    className="border-border hidden overflow-hidden rounded-md border bg-white sm:flex"
                  >
                    <button
                      type="button"
                      aria-pressed={resultsView === 'liste'}
                      aria-label="Affichage en liste (une colonne)"
                      title="Affichage en liste"
                      onClick={() => setResultsView('liste')}
                      className={cn(
                        'inline-flex h-9 w-10 items-center justify-center transition-colors',
                        resultsView === 'liste'
                          ? 'bg-quipux-navy-950 text-white'
                          : 'text-quipux-navy-700 hover:bg-quipux-navy-50',
                      )}
                    >
                      <List className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-pressed={resultsView === 'grille'}
                      aria-label="Affichage en grille (deux colonnes)"
                      title="Affichage en grille"
                      onClick={() => setResultsView('grille')}
                      className={cn(
                        'border-border inline-flex h-9 w-10 items-center justify-center border-l transition-colors',
                        resultsView === 'grille'
                          ? 'bg-quipux-navy-950 text-white'
                          : 'text-quipux-navy-700 hover:bg-quipux-navy-50',
                      )}
                    >
                      <LayoutGrid className="size-4" />
                    </button>
                  </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    // « Tout » est borné par FR-03 : au-delà de 10 amendes,
                    // seules les 10 premières entrent dans la sélection.
                    setSelected(
                      new Set(
                        contraventions
                          .slice(0, MAX_SELECTED_LIQUIDATIONS)
                          .map((item) => item.liquidationNumber),
                      ),
                    )
                  }
                >
                  {contraventions.length > MAX_SELECTED_LIQUIDATIONS
                    ? `Sélectionner les ${MAX_SELECTED_LIQUIDATIONS} premières`
                    : 'Tout sélectionner'}
                </Button>
                </div>
              </div>

              {/* En grille, deux colonnes à partir de `sm` seulement : en
                * dessous, une colonne reste la seule disposition lisible. */}
              <div className={cn('grid gap-3', resultsView === 'grille' && 'sm:grid-cols-2')}>
                {contraventions.map((item) => {
                  const isSelected = selected.has(item.liquidationNumber);
                  return (
                    <ContraventionCard
                      key={item.liquidationNumber}
                      liquidationNumber={item.liquidationNumber}
                      infractionType={item.infractionType}
                      amount={item.amount}
                      infractionDate={item.infractionDate}
                      dueDate={item.dueDate}
                      selected={isSelected}
                      action={
                        <Button
                          type="button"
                          variant={isSelected ? 'outline' : 'secondary'}
                          className="w-full"
                          onClick={() => toggle(item.liquidationNumber)}
                        >
                          {isSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                        </Button>
                      }
                    />
                  );
                })}
              </div>
            </section>
          )}

          {searched && !showPendingState && uiState === 'empty' && (
            <PortalState
              status="empty"
              title="Aucune contravention trouvée"
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsSearching(false);
                    setSearched(false);
                  }}
                >
                  Modifier la plaque
                </Button>
              }
            >
              Une plaque sans contravention n'est pas une erreur. Vérifiez la saisie ou réessayez
              plus tard.
            </PortalState>
          )}
        </div>

        {showPaymentSummary && (
          <aside className="border-border fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 p-3 backdrop-blur lg:sticky lg:inset-auto lg:top-6 lg:z-auto lg:self-start lg:border-t-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0">
            <PaymentSummary
              className="mx-auto max-w-5xl lg:max-w-none"
              selectedCount={selectedItems.length}
              totalAmount={totalAmount}
              action={
                canContinue ? (
                  <Button asChild size="lg" className="w-full">
                    <Link to={paymentHref}>Continuer vers le paiement</Link>
                  </Button>
                ) : (
                  // `disabled` est inopérant sur un <a> rendu via asChild :
                  // un vrai <button> désactivé est nécessaire pour bloquer
                  // le parcours (panier vide ou plafond 2 M dépassé).
                  <Button size="lg" className="w-full" disabled>
                    Continuer vers le paiement
                  </Button>
                )
              }
            />
            {selectedItems.length >= MAX_SELECTED_LIQUIDATIONS && (
              <p className="text-muted-foreground mx-auto mt-2 max-w-5xl text-xs lg:max-w-none">
                Maximum {MAX_SELECTED_LIQUIDATIONS} contraventions par paiement — désélectionnez-en
                une pour en ajouter une autre.
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
